import { ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ReturnItem, ReturnOrderLine } from 'returnCenter-api';
import { ExtendedReturnItem } from 'store/reducers/shared/sliceTypes';
import { TableRow } from 'components/ui/Table';
import { alignFlexStart, getCellPadding } from 'utils/helpers';

import ProductInfoCell from 'components/TableCells/ProductInfoCell';
import OrderLineNumberCell from 'components/TableCells/OrderLineNumberCell';
import QuantityRequestedCell from 'components/TableCells/QuantityRequestedCell';
import { commentTooltip } from 'components/TableCells/CommentIndicatorCell';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMessageDots } from '@fortawesome/pro-light-svg-icons';
import Flex from 'components/ui/Flex';
import Text from 'components/ui/Text';

interface UseBatchReturnTableRowsOptions {
    /**
     * The return slip the line was received on. The rows of a package all go to one slip per
     * receiving company, so this replaces the order line's list of every return on it, which
     * pointed at the wrong returns once several returns shared an order.
     */
    renderReturnSlip: (orderline: ReturnOrderLine) => ReactNode;
    /**
     * Only returns the customer asked for themselves have a requested quantity. Decided once for the
     * whole batch, so every package card has the same columns.
     */
    showRequested: boolean;
}

/**
 * A package's lines as the batch sees them: what was delivered, what the customer asked to return,
 * what the sender put in the batch and what the receiver took in, side by side, so a deviation shows
 * on the row it happened on.
 */
export const useBatchReturnTableRows = ({ renderReturnSlip, showRequested }: UseBatchReturnTableRowsOptions) => {
    const { t } = useTranslation();

    const headers: React.ReactNode[] = useMemo(
        () => [
            t('orderLineNumber', '#'),
            t('product', 'Product'),
            t('batchReturn.delivered', 'Delivered'),
            ...(showRequested ? [t('batchReturn.requested', 'Requested')] : []),
            t('batchReturn.sent', 'Sent'),
            t('batchReturn.receivedQty', 'Received'),
            t('reason', 'Reason'),
            t('batchReturn.returnSlip', 'Return slip'),
        ],
        [t, showRequested],
    );

    const renderReceived = useCallback(
        (item: ReturnItem) => {
            if (item.batchMissing) {
                return (
                    <CellText bold color="error">
                        {t('batchReturn.missingShort', 'Missing')}
                    </CellText>
                );
            }
            // Known once the receiver's slip for the row is handled.
            if (item.batchReceivedQuantity == null) return <CellText color="textLight">-</CellText>;
            // Short or over: either is a deviation.
            const deviates = item.batchReceivedQuantity !== (item.quantityInReturn ?? 0);
            return (
                <CellText bold={deviates} color={deviates ? 'error' : undefined}>
                    {item.batchReceivedQuantity}
                </CellText>
            );
        },
        [t],
    );

    const renderReason = useCallback(
        (item: ReturnItem) => {
            // Read only here, so a plain icon carries the comments rather than the slip's edit button.
            const comments = commentTooltip(item, t);
            return (
                <Flex align="center" gap="0.375rem">
                    <CellText>
                        {item.batchReasonName ?? '-'}
                        {item.batchReceivedReasonName && (
                            <span title={t('batchReturn.reasonChanged', 'Changed by the receiving company')}>
                                {' → '}
                                <b>{item.batchReceivedReasonName}</b>
                            </span>
                        )}
                    </CellText>
                    {comments && <FontAwesomeIcon icon={faMessageDots} title={comments} aria-label={comments} />}
                </Flex>
            );
        },
        [t],
    );

    const getRows = useCallback(
        (orderId: string, orderLines: ReturnOrderLine[]): TableRow[] => {
            return orderLines.flatMap(
                (orderline) =>
                    orderline.returnItems?.map((returnItem: ExtendedReturnItem, idx) => {
                        const itemId = returnItem.id ?? returnItem.splitId;
                        const cell = (el: ReactNode) => ({
                            el,
                            align: alignFlexStart(returnItem),
                            padding: getCellPadding(idx),
                        });

                        return {
                            key: String(itemId ?? `${orderId}-${orderline.orderLineNumber}-${idx}`),
                            cells: [
                                cell(idx === 0 && <OrderLineNumberCell orderLineNumber={orderline.orderLineNumber} />),
                                {
                                    ...cell(idx === 0 && <ProductInfoCell item={orderline} orderNumber={orderId} />),
                                    ean: orderline.ean ?? '',
                                    sku: orderline.sku ?? '',
                                },
                                cell(idx === 0 && <CellText>{orderline.quantityShipped ?? 0}</CellText>),
                                ...(showRequested
                                    ? [cell(<QuantityRequestedCell item={returnItem} itemId={itemId} />)]
                                    : []),
                                cell(<CellText>{returnItem.quantityInReturn ?? 0}</CellText>),
                                cell(renderReceived(returnItem)),
                                { ...cell(renderReason(returnItem)), flex: 1 },
                                cell(idx === 0 && renderReturnSlip(orderline)),
                            ],
                            removeGap: idx !== 0,
                        };
                    }) ?? [],
            );
        },
        [renderReturnSlip, renderReceived, renderReason, showRequested],
    );

    // #, product, the quantity columns, reason, return slip.
    const colLayout = `max-content minmax(180px, 2fr) repeat(${
        showRequested ? 4 : 3
    }, minmax(70px, 0.6fr)) minmax(140px, 1.5fr) minmax(90px, 1fr)`;

    return { headers, getRows, colLayout };
};

export default useBatchReturnTableRows;

const CellText = styled(Text)`
    font-family: inherit;
    font-size: 13px;
`;
